/**
 * Beta Ending Banner Component
 * 
 * 베타 기간 종료 D-day 카운트다운 배너
 * 2026년 2월 21일 ~ 2월 28일까지 표시
 * Max 플랜 사용자에게만 표시 (베타 혜택 수혜자)
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';

// 베타 종료일: 2026년 2월 28일 23:59:59 (KST)
const BETA_END_DATE = new Date('2026-02-28T23:59:59+09:00');
// 배너 표시 시작일: 2026년 2월 21일 00:00:00 (KST)
const BANNER_START_DATE = new Date('2026-02-21T00:00:00+09:00');

export default function BetaEndingBanner() {
  const { user, quota } = useAuth();
  const { lang } = useLanguage();
  const [isDismissed, setIsDismissed] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  
  useEffect(() => {
    const now = new Date();
    
    // 배너 표시 기간 체크 (2/21 ~ 2/28, KST)
    if (now < BANNER_START_DATE || now > BETA_END_DATE) {
      setDaysRemaining(null);
      return;
    }
    
    // D-day 계산
    const diffTime = BETA_END_DATE.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysRemaining(diffDays);
  }, []);
  
  // 로그인하지 않았거나 Max 플랜이 아니면 표시하지 않음
  if (!user || quota?.plan !== 'max') {
    return null;
  }
  
  // 배너 표시 기간이 아니면 표시하지 않음
  if (daysRemaining === null) {
    return null;
  }
  
  // 사용자가 닫았으면 표시하지 않음 (세션 동안만 유지)
  if (isDismissed) {
    return null;
  }
  
  const isLastDay = daysRemaining <= 1;
  const isUrgent = daysRemaining <= 3;
  
  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[100] ${
        isLastDay 
          ? 'bg-status-danger/95' 
          : isUrgent 
            ? 'bg-status-warning/95' 
            : 'bg-gradient-to-r from-[#6366f1]/95 to-[#8b5cf6]/95'
      } backdrop-blur-sm shadow-lg`}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* 아이콘 */}
          <div className={`w-8 h-8 rounded-full ${
            isLastDay ? 'bg-white/20' : isUrgent ? 'bg-black/10' : 'bg-white/20'
          } flex items-center justify-center flex-shrink-0`}>
            <span className="text-lg">
              {isLastDay ? '⚡' : isUrgent ? '⏰' : '🎁'}
            </span>
          </div>
          
          {/* 메시지 */}
          <div className={`text-sm ${isLastDay || !isUrgent ? 'text-white' : 'text-black/80'}`}>
            <span className="font-bold">
              {lang === 'ko' 
                ? `베타 혜택 종료 D-${daysRemaining}` 
                : `Beta Benefits End in D-${daysRemaining}`}
            </span>
            <span className="ml-2">
              {lang === 'ko' 
                ? isLastDay 
                  ? '오늘 자정에 베타 무료 혜택이 종료됩니다! (내일부터 Free 전환)'
                  : '베타 무료 혜택이 곧 종료됩니다. 계속 사용하려면 플랜을 선택하세요!'
                : isLastDay 
                  ? 'Beta benefits end at midnight tonight! (Switches to Free tomorrow)'
                  : 'Beta benefits are ending soon. Choose your plan to keep going!'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* 요금제 보기 버튼 */}
          <a
            href={lang === 'ko' ? '/pricing/ko.html' : '/pricing/en.html'}
            target="_blank"
            rel="noopener noreferrer"
            className={`px-3 py-1.5 text-xs font-bold rounded transition-colors ${
              isLastDay || !isUrgent
                ? 'bg-white text-[#6366f1] hover:bg-white/90'
                : 'bg-black/80 text-white hover:bg-black/90'
            }`}
          >
            {lang === 'ko' ? '요금제 보기 →' : 'View Plans →'}
          </a>
          
          {/* 닫기 버튼 */}
          <button
            onClick={() => setIsDismissed(true)}
            className={`p-1.5 rounded-full transition-colors ${
              isLastDay || !isUrgent
                ? 'text-white/80 hover:text-white hover:bg-white/20' 
                : 'text-black/60 hover:text-black/80 hover:bg-black/10'
            }`}
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

